import { hasProp, isPlainObject, isString } from 'remeda'
import { iconVars } from '../utils/dom'
import { createTemplateRenderer } from '../utils/fast-renderer'

type IconTextTone = 'neutral' | 'success' | 'danger'

type IconTextDisplay = {
  readonly label: string
  readonly meta: string
  readonly icon: string
  readonly tone: IconTextTone
}

const ICON_TEXT_TEMPLATE =
  '<span class="grid-icon-text grid-icon-text--neutral">' +
  '<span class="grid-icon-text__icon" data-component=".icon"><i></i></span>' +
  '<span class="grid-icon-text__content">' +
  '<span class="grid-icon-text__label"></span>' +
  '<span class="grid-icon-text__meta"></span>' +
  '</span>' +
  '</span>'

const TONE_CLASSES = {
  neutral: 'grid-icon-text--neutral',
  success: 'grid-icon-text--success',
  danger: 'grid-icon-text--danger'
} as const satisfies Record<IconTextTone, string>

const TONE_CLASS_LIST = [
  TONE_CLASSES.neutral,
  TONE_CLASSES.success,
  TONE_CLASSES.danger
] as const

const stringField = (
  data: unknown,
  field: 'value' | 'label' | 'meta' | 'icon' | 'tone'
): string => {
  if (!(isPlainObject(data) && hasProp(data, field))) {
    return ''
  }

  return isString(data[field]) ? data[field] : ''
}

const toneFrom = (value: string): IconTextTone =>
  value === 'success' || value === 'danger' ? value : 'neutral'

const displayFrom = (data: unknown): IconTextDisplay => {
  const value = stringField(data, 'value')
  const label = stringField(data, 'label')

  return {
    label: label || value,
    meta: stringField(data, 'meta'),
    icon: stringField(data, 'icon'),
    tone: toneFrom(stringField(data, 'tone'))
  }
}

const updateIconText = (root: HTMLElement, data: unknown): void => {
  const display = displayFrom(data)
  const iconElement = root.querySelector<HTMLElement>('.grid-icon-text__icon')
  const icon = iconElement?.querySelector<HTMLElement>('i')
  const label = root.querySelector<HTMLElement>('.grid-icon-text__label')
  const meta = root.querySelector<HTMLElement>('.grid-icon-text__meta')

  if (!(iconElement && icon && label && meta)) {
    return
  }

  root.classList.remove(...TONE_CLASS_LIST)
  root.classList.add(TONE_CLASSES[display.tone])
  root.title = display.meta ? `${display.label} ${display.meta}` : display.label

  label.textContent = display.label
  meta.textContent = display.meta
  meta.hidden = !display.meta

  if (!display.icon) {
    iconElement.hidden = true
    icon.style.removeProperty('--icon-url')
    return
  }

  const vars = iconVars(display.icon, 'xs')

  icon.style.setProperty('--icon-size', vars['--icon-size'])
  icon.style.setProperty('--icon-url', vars['--icon-url'])
  iconElement.hidden = false
}

export const IconTextComponent = createTemplateRenderer<unknown>(
  ICON_TEXT_TEMPLATE,
  updateIconText
)
